import * as i0 from '@angular/core';
import { InjectionToken, Injectable, NgModule } from '@angular/core';
import { isEqual, isNil, keys, join } from 'lodash-es';
import { throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';

var StorageLocation;
(function (StorageLocation) {
    StorageLocation["Local"] = "localStorage";
    StorageLocation["Session"] = "sessionStorage";
})(StorageLocation || (StorageLocation = {}));
var ResponseMode;
(function (ResponseMode) {
    ResponseMode["Query"] = "query";
    ResponseMode["Fragment"] = "fragment";
})(ResponseMode || (ResponseMode = {}));
const ADB2C_CONFIG = new InjectionToken('ADB2C_CONFIG');

var MfaMethod;
(function (MfaMethod) {
    MfaMethod["Phone"] = "phone";
    MfaMethod["Email"] = "email";
})(MfaMethod || (MfaMethod = {}));

class MfaRequest {
    primaryEmailAddress;
    method;
    value;
    additionalClaims;
    constructor(primaryEmailAddress = null, method = MfaMethod.Phone, value = null, additionalClaims) {
        this.primaryEmailAddress = primaryEmailAddress;
        this.method = method;
        this.value = value;
        this.additionalClaims = additionalClaims;
    }
}

class TokenRequest {
    constructor(mfaConfig, request) {
        this['B2CTenant'] = mfaConfig.tenant;
        this['B2CPolicy'] = mfaConfig.policy;
        this['ReplyUrl'] = mfaConfig.redirectUri;
        this['ClientId'] = mfaConfig.clientId;
        this['Claim1Name'] = 'email';
        this['Claim1Value'] = request.primaryEmailAddress;
        this['Claim2Name'] = 'mfaByPhoneOrEmail';
        this['Claim2Value'] = request.method;
        let claimId = 3;
        if (isEqual(request.method, MfaMethod.Phone)) {
            this[`Claim${claimId}Name`] = 'mfaPhoneNumber';
            this[`Claim${claimId}Value`] = request.value;
            claimId++;
        }
        if (!isNil(request.additionalClaims)) {
            keys(request.additionalClaims).forEach((claimName) => {
                this[`Claim${claimId}Name`] = claimName;
                this[`Claim${claimId}Value`] = request.additionalClaims[claimName];
                claimId++;
            });
        }
    }
}

const INITIAL_AUTH_STATE = {
    isLoggedIn: false,
    accessToken: null,
    idToken: null,
};
class AuthProvider {
    constructor() {
    }
    get loginComplete$() {
        throw new Error('Not Supported');
    }
    static ɵfac = function AuthProvider_Factory(t) { return new (t || AuthProvider)(); };
    static ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthProvider, factory: AuthProvider.ɵfac });
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthProvider, [{
        type: Injectable
    }], function () { return []; }, null); })();

class Adb2cUtilsService {
    constructor() { }
    getRandomNumber() {
        return Math.random();
    }
    getRandomString(length) {
        return Math.random()
            .toString(16)
            .slice(0 - (Math.abs(length) || 4));
    }
    getRandomId() {
        return `${this.getRandomString(4)}${this.getRandomString(4)}-${this.getRandomString(4)}-${this.getRandomString(4)}-${this.getRandomString(4)}-${this.getRandomString(4)}${this.getRandomString(4)}${this.getRandomString(4)}`;
    }
    convertToQueryString(extraQueryParameters) {
        let additionalParams = '';
        if (!isNil(extraQueryParameters)) {
            const qParams = [];
            keys(extraQueryParameters).forEach((name) => qParams.push(`${name}=${encodeURIComponent(extraQueryParameters[name])}`));
            additionalParams = join(qParams, '&');
        }
        return additionalParams;
    }
    getUrlParams(url) {
        const hashes = url.slice(url.indexOf('?') + 1).split('&');
        const params = {};
        hashes.map((hash) => {
            const [key, val] = hash.split('=');
            params[key] = decodeURIComponent(val);
        });
        return params;
    }
    isValidAdb2cConfiguration(adb2cConfig) {
        return (!isNil(adb2cConfig) &&
            !isNil(adb2cConfig.stateKey) &&
            !isNil(adb2cConfig.tenant) &&
            !isNil(adb2cConfig.clientId) &&
            !isNil(adb2cConfig.policy) &&
            !isNil(adb2cConfig.redirectUri) &&
            !isNil(adb2cConfig.scopes) &&
            adb2cConfig.scopes.length > 0);
    }
    getImplicitFlowAuthorityUrl(adb2cConfig) {
        if (isNil(adb2cConfig.authority)) {
            return `https://${this.getInstanceUrl(adb2cConfig)}/${this.getTenantUrl(adb2cConfig)}/${adb2cConfig.policy}/oauth2/v2.0`;
        }
        else {
            return adb2cConfig.authority;
        }
    }
    getTenantUrl(adb2cConfig) {
        return adb2cConfig.tenant.endsWith('chubb.com')
            ? `${adb2cConfig.tenant}`
            : `${adb2cConfig.tenant}.onmicrosoft.com`;
    }
    getInstanceUrl(adb2cConfig) {
        return adb2cConfig.tenant.endsWith('chubb.com')
            ? `${adb2cConfig.tenant}`
            : `${adb2cConfig.tenant}.b2clogin.com`;
    }
    getMsalAuthorityUrl(adb2cConfig) {
        if (isNil(adb2cConfig.authority)) {
            return `https://${this.getInstanceUrl(adb2cConfig)}/tfp/${this.getTenantUrl(adb2cConfig)}/${adb2cConfig.policy}`;
        }
        else {
            return adb2cConfig.authority;
        }
    }
    getLoginUrl(adb2cConfig, extraQueryParameters) {
        adb2cConfig.responseMode = adb2cConfig.responseMode || ResponseMode.Query;
        const loginUrl = `${this.getImplicitFlowAuthorityUrl(adb2cConfig)}/authorize?client_id=${adb2cConfig.clientId}&response_type=id_token+token&redirect_uri=${encodeURIComponent(adb2cConfig.redirectUri)}&response_mode=${adb2cConfig.responseMode}&scope=openid%20${encodeURIComponent(join(adb2cConfig.scopes, ' '))}`;
        const additionalParams = this.convertToQueryString(extraQueryParameters);
        return !isNil(additionalParams) && additionalParams.length > 0
            ? `${loginUrl}&${additionalParams}`
            : loginUrl;
    }
    getLogoutUrl(adb2cConfig, postLogoutUrl) {
        const logoutUrl = `${this.getImplicitFlowAuthorityUrl(adb2cConfig)}/logout`;
        return !isNil(postLogoutUrl) && postLogoutUrl.length > 0
            ? `${logoutUrl}?post_logout_redirect_uri=${encodeURI(postLogoutUrl)}`
            : logoutUrl;
    }
    static ɵfac = function Adb2cUtilsService_Factory(t) { return new (t || Adb2cUtilsService)(); };
    static ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: Adb2cUtilsService, factory: Adb2cUtilsService.ɵfac });
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Adb2cUtilsService, [{
        type: Injectable
    }], function () { return []; }, null); })();

var ApiErrorCodes;
(function (ApiErrorCodes) {
    ApiErrorCodes["API_ERROR"] = "[ERROR]API ERROR";
    ApiErrorCodes["INVALID_RESPONSE"] = "[ERROR]INVALID API RESPONSE";
})(ApiErrorCodes || (ApiErrorCodes = {}));

class B2cTokenBuilderService {
    _httpClient;
    constructor(_httpClient) {
        this._httpClient = _httpClient;
    }
    getMfaToken(uri, request) {
        return (this._httpClient.post(uri, request).pipe(catchError((error) => throwError({
            errorCode: ApiErrorCodes.API_ERROR,
            error: error,
        })), switchMap((response) => 'TokenData' in response
            ? of(response['TokenData'])
            : throwError({
                errorCode: ApiErrorCodes.INVALID_RESPONSE,
                error: response,
            }))));
    }
    static ɵfac = function B2cTokenBuilderService_Factory(t) { return new (t || B2cTokenBuilderService)(i0.ɵɵinject(i1.HttpClient)); };
    static ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: B2cTokenBuilderService, factory: B2cTokenBuilderService.ɵfac });
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(B2cTokenBuilderService, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }]; }, null); })();

class StorageUtilsService {
    _defaultStorageLocation = StorageLocation.Local;
    constructor() { }
    getItem(key, location) {
        return (location || this._defaultStorageLocation) === StorageLocation.Local
            ? window.localStorage.getItem(key)
            : window.sessionStorage.getItem(key);
    }
    setItem(key, value, location) {
        if ((location || this._defaultStorageLocation) === StorageLocation.Local) {
            window.localStorage.setItem(key, value);
        }
        else {
            window.sessionStorage.setItem(key, value);
        }
    }
    removeItem(key, location) {
        if ((location || this._defaultStorageLocation) === StorageLocation.Local) {
            window.localStorage.removeItem(key);
        }
        else {
            window.sessionStorage.removeItem(key);
        }
    }
    static ɵfac = function StorageUtilsService_Factory(t) { return new (t || StorageUtilsService)(); };
    static ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: StorageUtilsService, factory: StorageUtilsService.ɵfac });
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StorageUtilsService, [{
        type: Injectable
    }], function () { return []; }, null); })();

class AuthProviderFactory {
    _implementations = {};
    constructor() { }
    registerProvider(providerType, instance) {
        this._implementations[providerType] = instance;
    }
    getProvider(providerType) {
        return this._implementations[providerType];
    }
    static ɵfac = function AuthProviderFactory_Factory(t) { return new (t || AuthProviderFactory)(); };
    static ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthProviderFactory, factory: AuthProviderFactory.ɵfac });
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthProviderFactory, [{
        type: Injectable
    }], function () { return []; }, null); })();

class Adb2cUtilsModule {
    static ɵfac = function Adb2cUtilsModule_Factory(t) { return new (t || Adb2cUtilsModule)(); };
    static ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: Adb2cUtilsModule });
    static ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
            Adb2cUtilsService,
            B2cTokenBuilderService,
            StorageUtilsService,
            AuthProviderFactory,
        ], imports: [CommonModule, HttpClientModule] });
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Adb2cUtilsModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [CommonModule, HttpClientModule],
                providers: [
                    Adb2cUtilsService,
                    B2cTokenBuilderService,
                    StorageUtilsService,
                    AuthProviderFactory,
                ],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(Adb2cUtilsModule, { imports: [CommonModule, HttpClientModule] }); })();

/*
 * Public API Surface of chubb-auth-types
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ADB2C_CONFIG, Adb2cUtilsModule, Adb2cUtilsService, ApiErrorCodes, AuthProvider, AuthProviderFactory, B2cTokenBuilderService, INITIAL_AUTH_STATE, MfaMethod, MfaRequest, ResponseMode, StorageLocation, StorageUtilsService, TokenRequest };
